import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Wallet,
  Crown,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  CreditCard,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AddFunds = () => {
  const { user, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const isPro = user?.is_pro || (user?.total_deposited >= 1000);
  const balance = user?.balance || 0;
  const totalDeposited = user?.total_deposited || 0;
  const amountToPro = Math.max(0, 1000 - totalDeposited);

  const packages = [
    { id: "100", amount: 100, label: "$100" },
    { id: "250", amount: 250, label: "$250" },
    { id: "500", amount: 500, label: "$500" },
    { id: "1000", amount: 1000, label: "$1,000", recommended: !isPro },
    { id: "2000", amount: 2000, label: "$2,000" },
    { id: "5000", amount: 5000, label: "$5,000" },
  ];

  // Check payment status when returning from Stripe
  const checkPaymentStatus = useCallback(async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setCheckingPayment(false);
        toast.info("Payment status check timed out. Please refresh the page or check your email for confirmation.");
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`, {
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();

          if (data.payment_status === "completed" || data.already_processed) {
            setCheckingPayment(false);
            setPaymentSuccess(true);
            
            if (data.pro_unlocked) {
              toast.success("Congratulations! You've unlocked PRO status with discounted rates!", { duration: 5000 });
            } else {
              toast.success(`Payment successful! $${data.amount?.toFixed(2) || ''} has been added to your account.`);
            }
            
            checkAuth(); // Refresh user data
            fetchTransactions();
            
            // Clear URL params
            setSearchParams({});
            return;
          } else if (data.status === "expired") {
            setCheckingPayment(false);
            toast.error("Payment session expired. Please try again.");
            setSearchParams({});
            return;
          }
        }

        // Continue polling
        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };

    await poll();
  }, [checkAuth, setSearchParams]);

  useEffect(() => {
    fetchTransactions();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get("session_id");
    const status = searchParams.get("status");
    
    if (status === "cancelled") {
      toast.info("Payment was cancelled.");
      setSearchParams({});
    } else if (sessionId && status === "success") {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, checkPaymentStatus, setSearchParams]);

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/funds/transactions`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.items);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    } finally {
      setLoadingTx(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedPackage) {
      toast.error("Please select an amount");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          package_id: selectedPackage,
          origin_url: window.location.origin,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create checkout session");
      }
    } catch (error) {
      toast.error("Error initiating payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-fade-in">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Add Funds</h1>
        <p className="text-slate-600 mt-1">Top up your account to submit press releases</p>
      </div>

      {/* Payment Processing Overlay */}
      {checkingPayment && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              <div>
                <h2 className="text-lg font-semibold text-blue-900">Processing Payment...</h2>
                <p className="text-blue-700 text-sm">Please wait while we confirm your payment.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Success */}
      {paymentSuccess && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-emerald-500 p-3 rounded-xl">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-emerald-900">Payment Successful!</h2>
                <p className="text-emerald-700 text-sm">Your funds have been added to your account.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pro Status Banner */}
      {!isPro && (
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-amber-500 p-3 rounded-xl">
                <Crown className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-heading font-semibold text-slate-900 flex items-center gap-2">
                  Unlock PRO Status
                  <Badge className="bg-amber-500 text-white">Save up to 50%</Badge>
                </h2>
                <p className="text-slate-600 text-sm mt-1">
                  Deposit a total of $1,000 or more to unlock discounted rates on all distribution channels.
                </p>
                <p className="text-amber-700 font-semibold mt-2">
                  You need ${amountToPro.toFixed(2)} more to unlock PRO rates!
                </p>
              </div>
              <Button 
                onClick={() => setSelectedPackage("1000")}
                className="bg-amber-500 hover:bg-amber-600"
              >
                Add $1,000
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isPro && (
        <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-emerald-500 p-3 rounded-xl">
                <Crown className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-heading font-semibold text-emerald-700 flex items-center gap-2">
                  PRO Account Active
                  <Badge className="bg-emerald-500 text-white">Discounted Rates</Badge>
                </h2>
                <p className="text-slate-600 text-sm mt-1">
                  You're enjoying discounted rates on all distribution channels!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Balance Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Current Balance</p>
                <p className="text-3xl font-bold text-slate-900">${balance.toFixed(2)}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-xl">
                <Wallet className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Deposited</p>
                <p className="text-3xl font-bold text-slate-900">${totalDeposited.toFixed(2)}</p>
              </div>
              <div className="bg-emerald-100 p-3 rounded-xl">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Account Status</p>
                <p className="text-2xl font-bold text-slate-900">{isPro ? "PRO" : "Standard"}</p>
              </div>
              <div className={`p-3 rounded-xl ${isPro ? 'bg-amber-100' : 'bg-slate-100'}`}>
                <Crown className={`h-6 w-6 ${isPro ? 'text-amber-600' : 'text-slate-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Funds Form */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Add Funds to Your Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-sm text-slate-600 mb-4">Select an amount to add:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {packages.map((pkg) => (
                <Button
                  key={pkg.id}
                  variant={selectedPackage === pkg.id ? "default" : "outline"}
                  className={`h-16 flex flex-col items-center justify-center relative ${
                    selectedPackage === pkg.id ? "bg-blue-600 ring-2 ring-blue-300" : ""
                  }`}
                  onClick={() => setSelectedPackage(pkg.id)}
                  data-testid={`package-${pkg.id}`}
                >
                  <span className="text-lg font-bold">{pkg.label}</span>
                  {pkg.recommended && (
                    <Badge className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs">
                      PRO
                    </Badge>
                  )}
                </Button>
              ))}
            </div>
          </div>

          {selectedPackage && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Selected Amount</p>
                  <p className="text-2xl font-bold text-slate-900">
                    ${packages.find(p => p.id === selectedPackage)?.amount.toFixed(2)}
                  </p>
                </div>
                <Button
                  onClick={handleCheckout}
                  disabled={loading || checkingPayment}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="checkout-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay with Stripe
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>💡 PRO Tip:</strong> Deposit $1,000 or more total to unlock discounted rates on all distribution channels and save up to 50% on every press release!
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>🔒 Secure Payment:</strong> All payments are processed securely through Stripe. We never store your card details.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-500" />
            Transaction History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTx ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Wallet className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <div
                  key={tx.transaction_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${tx.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                      {tx.amount > 0 ? (
                        <TrendingUp className={`h-4 w-4 text-emerald-600`} />
                      ) : (
                        <DollarSign className={`h-4 w-4 text-red-600`} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{tx.description}</p>
                      <p className="text-sm text-slate-500">
                        {format(new Date(tx.created_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {tx.amount > 0 ? '+' : ''}${Math.abs(tx.amount).toFixed(2)}
                    </p>
                    <p className="text-sm text-slate-500">
                      Balance: ${tx.balance_after.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AddFunds;
